<template>
	<div
		class="add-to-cart"
		:class="{ isLoading: loading }"
	>
		<TnButton
			primary
			:size="size"
			:block="size !== 'xs'"
			@click="addToCart"
			:disabled="isDisabled || added"
			:text="added ? 'Lagt til' : cText"
			:icon-left="(added && 'success') || ''"
			:loading="loading"
			href="javascript:"
		>
		</TnButton>
		<TnNotification
			position="bottom-right"
			type="error"
			title="Noe gikk galt"
			timeout="5"
			v-if="error"
		/>
		<ECommerceModal
			:modalContent="modalContent"
			:modal="modal"
			:disabled="!!error"
			@confirmPrompt="add"
			@closeModal="modal = !modal"
		/>
	</div>
</template>

<script setup>
import { useStore } from "vuex";
import ShoppingCartItemModel from "~/helpers/ecommerce/ShoppingCartItemModel/ShoppingCartItemModel";
import { isAccessory, isDevice } from "~/helpers/ecommerce/hardwareOrderHelper";
import { v4 as uuid } from "uuid";
import safeUrl from "~/helpers/ecommerce/safeEcommerceUrl";
import { pickBy } from "~/helpers/object-utility";
import { getSubCategory } from "../helpers/ecommerceTrackingHelper";
import { ProductCategory } from "~/components/ecommerce/types/ProductCategory";

const props = defineProps({
	product: {
		type: Object,
		required: true,
	},
	isSubItem: {
		type: Boolean,
		default: false,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	size: {
		type: String,
		default: "m",
	},
	text: {
		type: String,
		default: "Legg i handlekurv",
	},
	added: {
		type: Boolean,
		default: false,
	},
	segment: {
		type: String,
		required: false,
		default: "",
	},
	ignoreStock: {
		type: Boolean,
		default: false,
	},
});

const store = useStore();

const { updateGrading } = useTradeIn();

const loading = ref(false);
const error = ref(false);
const modal = ref(false);

const isTest = computed(() => store?.state?.isTest);

const selectedWebShopOption = computed(() => store.state.ecommerce.selectedWebShopOption);
const categoriesThatDontRequireStock = computed(() => store.state.ecommerce.categoriesThatDontRequireStock);
const visibleCartItems = computed(() => store.getters["shoppingCart/visibleCartItems"]);
const currentTerminal = computed(() => store.getters["checkout/currentTerminal"]);

const stockThresholdMet = computed(() => props.product?.stockThreshold > props.product?.inventory?.[0]?.amount);

const outOfStock = computed(
	() =>
		(props.product?.inventory?.[0]?.amount < 1 &&
			!categoriesThatDontRequireStock.value.includes(props.product?.category)) ||
		stockThresholdMet.value,
);

const isDisabled = computed(() =>
	props.ignoreStock && isTest.value
		? false
		: props.disabled || loading.value || outOfStock.value || stockThresholdMet.value,
);

const swapPhoneExist = computed(() =>
	visibleCartItems.value.some((it) => it.type === "old-swap-device" && it.metaData?.hasSwap),
);

const containsTradeInOrSwapIn = computed(() => visibleCartItems.value.some((it) => it.type === "old-swap-device"));

const cText = computed(() =>
	(outOfStock.value && props.disabled) ||
	(props.product?.inventory?.[0]?.amount < 1 && props.disabled) ||
	outOfStock.value
		? "Ikke på lager"
		: props.text,
);

const productLink = computed(() => ({
	to: safeUrl(props.product?.category, props.product?.brand, props.product?.title, props.product?.condition),
}));

const campaign = computed(() => {
	const includedProductId =
		props.product?.campaignData?.attributes?.includedItem || props.product?.memberPrice?.campaignData?.includedItem;
	const name = props.product?.campaignData?.name || props.product?.memberPrice?.campaignData?.name;
	return pickBy({ includedProductId, name });
});

const replaceDeviceInCart = computed(() => {
	const deviceCategories = [
		ProductCategory.PHONE,
		ProductCategory.GOOD_AS_NEW,
		ProductCategory.WATCH,
		ProductCategory.TABLET,
	];
	return (
		deviceCategories.includes(props.product?.category) &&
		visibleCartItems.value?.some((item) => deviceCategories.includes(item?.metadata?.category))
	);
});

const deviceNames = computed(() => ({
	currentDevice: visibleCartItems.value?.find((item) => isDevice(item))?.metadata?.name,
	newDevice: props.product?.cleanName,
}));

const modalContent = computed(() => ({
	title: props.modalProps?.title || "Vil du bytte enhet?",
	text:
		props.modalProps?.text ||
		`For å kunne legge til en enhet i handlekurven, må du først fullføre bestillingen for enheten som allerede ligger i handlekurven. <br/> Legger du til en ny enhet før du har fullført bestillingen, byttes enheten ut.`,
	confirmButtonText: props.modalProps?.confirmButtonText || `Bytt til ${deviceNames.value?.newDevice}`,
	cancelButtonText: props.modalProps?.cancelButtonText || `Fortsett med ${deviceNames.value?.currentDevice}`,
	deviceNames: deviceNames.value,
}));

const addToCart = () => {
	if (replaceDeviceInCart.value) {
		modal.value = !modal.value;
	} else {
		add();
	}
};

const add = async () => {
	if (isDisabled.value) {
		return;
	}

	store.commit("checkout/setNeedSubscription", false);

	await addProduct();

	if (containsTradeInOrSwapIn.value && !swapPhoneExist.value && isDevice(props.product)) {
		await updateGrading(props.product?.gtin);
	}

	modal.value = false;
};

const addProduct = async (openCart = true) => {
	if ((props.ignoreStock && isTest.value) || (window.shoppingCart && !props.disabled)) {
		try {
			const cartHasDevice = visibleCartItems.value?.some((item) => isDevice(item));
			const existingAccessories = cartHasDevice ? undefined : visibleCartItems.value?.filter(isAccessory);
			const downPayment = selectedWebShopOption.value?.includes("DOWN_PAYMENT");
			if (visibleCartItems.value.length > 0 && !props.isSubItem && !containsTradeInOrSwapIn.value) {
				await store.dispatch("shoppingCart/emptyCart");
			} else if (
				visibleCartItems.value.length > 0 &&
				currentTerminal.value &&
				!props.isSubItem &&
				containsTradeInOrSwapIn.value
			) {
				await store.dispatch("shoppingCart/remove", currentTerminal.value?.id);
			}

			const downPaymentPlan = {
				...store.state.ecommerce.selectedDownPaymentProduct,
				amountUpFront: store.state.ecommerce.amountUpFront,
			};
			loading.value = true;
			error.value = false;

			const description = `${props.product.memory || props.product.size || ""} ${props.product.color}`.trim();
			const metadata = pickBy({
				name: props.product.cleanName,
				description: description,
				color: props.product.color,
				model: props.product.model,
				brand: props.product.brand,
				memory: props.product.memory,
				image: props.product.imageLink,
				link: productLink.value || null,
				category: props.product.category,
				subCategory: getSubCategory(props.product.categories),
				swapMonthlyPrice: props.product?.swapMonthlyPrice,
				recurringPrice: props.product.swapMonthlyPrice,
				swapTotalPrice: props.product?.swapTotalPrice,
				swapDiscountedTotalPrice:
					props.product?.swapDiscountedTotalPrice || props.product?.swapDiscountedMonthlyPrice * 24,
				saleRecurringPrice: props.product?.swapDiscountedMonthlyPrice,
				price: props.product?.priceWithVat || undefined,
				priceWithoutVat: props.product?.price || undefined,
				salePrice: props.product?.salePriceWithVat || undefined,
				salePriceWithoutVat: props.product?.salePrice || undefined,
				campaign: campaign.value,
				installmentsInMonths: downPaymentPlan?.monthlyInstallments,
				downPayment,
				gtin: props.product?.gtin,
				stockThreshold: props.product?.stockThreshold || undefined,
				sapProductId: props.product?.sapProductId || undefined,
				memberPrice: props.product?.memberPrice || undefined,
				prices: props.product?.prices,
				inventory: props.product?.inventory,
				disablePorterbuddy: props.product?.disablePorterbuddy,
				disableDownPayment: props.product?.disableDownPayment,
				downPaymentPlan: downPayment ? downPaymentPlan : undefined,
			});

			let shoppingCartItem;
			if (props.isSubItem && cartHasDevice) {
				shoppingCartItem = ShoppingCartItemModel(
					"hardware",
					{ id: props.product.id },
					metadata,
					currentTerminal.value?.id,
				);
			} else if (
				[ProductCategory.SOUND, ProductCategory.ACCESSORY, ProductCategory.ROUTER].includes(props.product?.category)
			) {
				shoppingCartItem = ShoppingCartItemModel("hardware", { id: props.product.id }, metadata, uuid());
			} else {
				shoppingCartItem = ShoppingCartItemModel(
					"hardware",
					{ id: props.product.id },
					metadata,
					uuid(),
					existingAccessories,
				);
			}
			await window.shoppingCart.add(shoppingCartItem);
			if (openCart && !store.getters["shoppingCart/open"]) {
				window.shoppingCart.toggle();
			}
		} catch (err) {
			console.log(err);
			error.value = true;
		} finally {
			loading.value = false;
		}
	}
};
</script>

<style lang="scss" scoped></style>
